import { ReactNode, useEffect, useState } from 'react';
import {
  WorkOrderProduct,
  WorkOrderProductInput,
} from '../../../clients/quipli-service/service-and-repair/service-and-repair';
import { DataTable, DataTableColumn, DataTableRow } from '../../../components/table/data-table';
import { Typography } from '../../../components/typography/typography';
import { Button } from '../../../components/form/button';
import classNames from 'classnames';
import Icon from '../../../components/icons/icon';
import { WorkOrderPartsForm } from './work-order-part-form';
import { Location } from '../../../clients/quipli-service/location/location';
import { displayMoneyFormat, truncateString } from '../../../util/util';
import { DeleteAction, EditAction } from '../../../components/actions/actions';
import { ConfirmModal } from '../../../components/modal/confirm-modal';

export const WorkOrderPartsTable = ({
  location,
  workOrderProducts,
  onAddPart,
  onDeletePart,
  onEditPart,
  disabled = false,
  userId,
}: {
  location: Location;
  workOrderProducts: WorkOrderProduct[] | undefined;
  onAddPart: (input: WorkOrderProductInput) => void;
  onDeletePart: (workOrderProductId: string) => void;
  onEditPart: (input: WorkOrderProductInput) => void;
  disabled?: boolean;
  userId?: number;
}) => {
  // States

  const [data, setData] = useState<DataTableRow[]>([]);
  const [showPartsForm, setShowPartsForm] = useState<boolean>(false);
  const [actionModal, setActionModal] = useState<ReactNode>();
  const [partToEdit, setPartToEdit] = useState<WorkOrderProduct | undefined>();

  // Vars
  const columns: DataTableColumn[] = [
    { key: 'part', label: 'Name', className: '!py-[13px] !pl-[24px]' },
    { key: 'sku', label: 'SKU', className: '!py-[13px] !pl-[24px]' },
    { key: 'description', label: 'Description', className: '!py-[13px]' },
    { key: 'quantity', label: 'Quantity', className: '!py-[13px]' },
    { key: 'unitPrice', label: 'Unit Price', className: '!py-[13px]' },
    { key: 'buttons', label: 'Actions', fixedAsLast: true },
  ];

  const buildDataRow = (workOrderProduct: WorkOrderProduct): DataTableRow => {
    return {
      mobileHeader: (
        <>
          <div className="product-container">
            <Typography type="text-sm" className="text-quipli-black-800">
              {workOrderProduct.product.name}
            </Typography>
          </div>
        </>
      ),
      fields: {
        part: (
          <div className="product-container">
            <Typography type="text-sm" className="truncate text-quipli-black-800">
              {workOrderProduct.product.name}
            </Typography>
          </div>
        ),
        sku: (
          <div className="product-container">
            <Typography type="text-sm" className="truncate text-quipli-black-800">
              {workOrderProduct.product.sku}
            </Typography>
          </div>
        ),
        description: (
          <div className="product-container">
            <Typography type="text-sm" className="truncate text-quipli-black-800">
              {truncateString(workOrderProduct.description, 40)}
            </Typography>
          </div>
        ),
        quantity: (
          <div className="product-container">
            <Typography type="text-sm" className="truncate text-quipli-black-800">
              {workOrderProduct.quantity}
            </Typography>
          </div>
        ),
        unitPrice: (
          <div className="product-container">
            <Typography type="text-sm" className="truncate text-quipli-black-800">
              {displayMoneyFormat(workOrderProduct.customerUnitPrice, location?.currency)}
            </Typography>
          </div>
        ),
        buttons: !disabled && (
          <div className="button-group">
            <EditAction
              onClick={() => {
                setPartToEdit(workOrderProduct);
                setShowPartsForm(true);
              }}
              data-cy="edit_product"
            />

            <DeleteAction
              onClick={() =>
                setActionModal(
                  <ConfirmModal
                    isOpen={true}
                    close={() => setActionModal(undefined)}
                    onConfirm={() => {
                      onDeletePart(workOrderProduct.id);
                      setActionModal(undefined);
                    }}
                    headerIcon={<i className="fa fa-trash-can delete-icon"></i>}
                    prompt={`Are you sure you want to delete part ${workOrderProduct.product.name}?`}
                  />,
                )
              }
              data-cy="delete_product"
            ></DeleteAction>
          </div>
        ),
        id: workOrderProduct.id,
      },
    };
  };

  useEffect(() => {
    if (!workOrderProducts) return;

    setData(workOrderProducts.map(buildDataRow));
  }, []);

  useEffect(() => {
    if (!workOrderProducts) return;

    setData(workOrderProducts.map(buildDataRow));
  }, [workOrderProducts]);

  useEffect(() => {
    if (showPartsForm) return;

    setPartToEdit(undefined);
  }, [showPartsForm]);

  return (
    <>
      <div className="min-w-full flex gap-[4px] flex-col sm:flex-row justify-between items-start sm:items-center">
        <div className="flex gap-3 items-center shrink-0">
          <Typography type="text-md" weight="medium" className="text-grey-800">
            Parts & Services
          </Typography>
        </div>
        {!disabled && (
          <div className="flex gap-3 items-center w-full sm:w-fit justify-start sm:justify-end">
            <Button
              className={classNames('button button-secondary')}
              data-cy={`app_part`}
              onClick={() => setShowPartsForm(!showPartsForm)}
            >
              <Typography type="text-sm" weight="semi-bold" className="text-[var(--grey-700)] hidden md:inline">
                Add
              </Typography>
              <Icon icon="Plus" />
            </Button>
          </div>
        )}
      </div>
      <div className="min-w-full mt-[5px] flex flex-col">
        <DataTable columnNames={columns} data={data} showAlwaysDesktop={true} noDataText="" />
      </div>

      {actionModal}

      <WorkOrderPartsForm
        isOpen={showPartsForm}
        location={location}
        setIsOpen={setShowPartsForm}
        onAddPart={onAddPart}
        onEditPart={onEditPart}
        workOrderProduct={partToEdit}
        userId={userId}
      />
    </>
  );
};
